import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Star, Trash2 } from "lucide-react";

export const acctLabel = (a) => `${a.nickname || "Account"}${a.bank_name ? ` · ${a.bank_name}` : ""}${a.account_number ? ` ••${String(a.account_number).slice(-4)}` : ""}`;

export const useBankAccounts = (propertyId) => {
  const [data, setData] = useState({ accounts: [], default_bank_id: null });
  const reload = useCallback(() => {
    if (!propertyId) return Promise.resolve();
    return api.get(`/properties/${propertyId}/bank-accounts`).then((r) => setData(r.data)).catch(() => {});
  }, [propertyId]);
  useEffect(() => { reload(); }, [reload]);
  return { ...data, reload };
};

export const BankAccountSelect = ({ accounts, value, onChange, testid = "bank-account-select" }) => (
  <Select value={value || ""} onValueChange={(v) => onChange(v, accounts.find((a) => a.id === v))}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder="Choose account" /></SelectTrigger>
    <SelectContent>
      {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{acctLabel(a)} · next #{a.current_check_number ?? 1001}</SelectItem>)}
    </SelectContent>
  </Select>
);

const FIELDS = [
  ["nickname", "Nickname (e.g. Operating, Payroll)", "text"],
  ["company_name", "Company Name (on check)", "text"],
  ["bank_name", "Bank Name", "text"],
  ["routing_number", "Routing Number", "text"],
  ["account_number", "Account Number", "text"],
  ["starting_check_number", "Starting Check #", "number"],
  ["current_check_number", "Current Check #", "number"],
];
const NUMS = ["starting_check_number", "current_check_number", "print_offset_x", "print_offset_y"];

export const saveBankAccounts = (propertyId, accounts, defaultId) =>
  api.put(`/properties/${propertyId}/bank-accounts`, {
    accounts: accounts.map((a) => ({ ...a, ...Object.fromEntries(NUMS.filter((k) => a[k] !== undefined && a[k] !== "").map((k) => [k, Number(a[k])])) })),
    default_bank_id: defaultId,
  });

export const BankAccountsEditor = ({ propertyId, canEdit }) => {
  const { accounts, default_bank_id, reload } = useBankAccounts(propertyId);
  const [list, setList] = useState([]);
  const [def, setDef] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setList(accounts); setDef(default_bank_id); }, [accounts, default_bank_id]);
  const setA = (i, k, v) => setList((l) => l.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  const add = () => setList((l) => [...l, { id: `new-${Date.now()}`, nickname: "", company_name: l[0]?.company_name || "", bank_name: "", routing_number: "", account_number: "", starting_check_number: 1001, current_check_number: 1001, check_stock_type: l[0]?.check_stock_type || "blank" }]);
  const remove = (i) => {
    if (!window.confirm(`Remove "${list[i].nickname || "this account"}"? Checks already printed keep their numbers.`)) return;
    setList((l) => l.filter((_, j) => j !== i));
  };
  const save = async () => {
    setBusy(true);
    try {
      const clean = list.map((a) => (String(a.id).startsWith("new-") ? { ...a, id: null } : a));
      const defIdx = list.findIndex((a) => a.id === def);
      const r = await saveBankAccounts(propertyId, clean, defIdx >= 0 && clean[defIdx].id ? clean[defIdx].id : null);
      if (defIdx >= 0 && !clean[defIdx].id) await saveBankAccounts(propertyId, r.data.accounts, r.data.accounts[defIdx].id);
      toast.success("Bank accounts saved");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="rounded-none border p-6" data-testid="bank-settings">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-bold tracking-tight">Bank Accounts &amp; Checks</h3>
          <p className="text-sm text-muted-foreground">Each account has its own check numbering. The ★ default is pre-selected when printing checks.</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={add} data-testid="bank-add-account"><Plus className="mr-1.5 h-4 w-4" /> Add account</Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="bank-save-accounts">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save accounts</Button>
          </div>
        )}
      </div>
      <div className="space-y-4">
        {list.map((a, i) => (
          <div key={a.id} className={`border p-4 ${a.id === def ? "border-primary" : "border-border"}`} data-testid={`bank-account-${i}`}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="font-semibold">{a.nickname || `Account ${i + 1}`}</div>
              <div className="flex items-center gap-2">
                {a.id === def ? <span className="inline-flex items-center gap-1 text-xs font-bold text-primary" data-testid={`bank-default-badge-${i}`}><Star className="h-3.5 w-3.5 fill-current" /> Default</span>
                  : canEdit && <Button size="sm" variant="ghost" className="h-7" onClick={() => setDef(a.id)} data-testid={`bank-make-default-${i}`}><Star className="mr-1 h-3.5 w-3.5" /> Make default</Button>}
                {canEdit && list.length > 1 && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(i)} data-testid={`bank-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {FIELDS.map(([k, label, type]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  <Input type={type} value={a[k] ?? ""} disabled={!canEdit} onChange={(e) => setA(i, k, e.target.value)} data-testid={`bank-${i}-${k}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
